import React, { Component } from 'react';
import * as Yup from 'yup';
import { connect } from 'react-redux';
import { Container, Message } from 'semantic-ui-react';
import { Button, Checkbox, Dropdown, Form, Input, TextArea } from 'formik-semantic-ui';
import moment from 'moment';
import {
  EPtwStatus,
  IBuilding,
  IProject,
  IPtw,
  IPtwFile,
  ITrain,
  IUser,
  IVehicle,
  IWorkstation,
} from '@relab/alstom-ptw-shared';
import { Push, push } from 'connected-react-router';

import { ActionType, RootState, ServerRequestStatus } from '../../redux/types';
import { appState, appStateServerRequests } from '../../redux/selectors';
import {
  buildingsAction,
  ptwCreateAction,
  ptwEditAction,
  resetServerRequestAction,
  workstationsAction,
} from '../../redux/actions';
import { DatePicker } from '../DatePicker';
import { TimePickerComponent } from '../TimePickerComponent';
import { EhsCreateModal } from '../ehs/EhsCreateModal';
import { TranslationComponent } from '../TranslationComponent';
import i18n from 'i18next';
import { withTranslation } from 'react-i18next';

const PtwCreateSchema = (t: typeof i18n.t) =>
  Yup.object().shape({
    name: Yup.string().required(t('Required field')).ensure(),
    startDate: Yup.string().required(t('Required field')).ensure(),
    startTime: Yup.object().required(t('Required field')).nullable(true),
    endDate: Yup.string().required(t('Required field')).ensure(),
    endTime: Yup.object().required(t('Required field')).nullable(true),
    train: Yup.string().required(t('Required field')),
    vehicles: Yup.array().required(t('Required field')),
    building: Yup.string().required(t('Required field')),
    workstation: Yup.string().required(t('Required field')),
    pc9: Yup.string().required(t('Required field')),
    descriptionFile: Yup.string().nullable(true),
    ehs: Yup.string()
      .when('risk', {
        is: true,
        then: Yup.string().required(t('Required field')),
        otherwise: Yup.string().notRequired(),
      })
      .nullable(true),
  });

interface PtwCreateValues extends Partial<IPtw> {
  startDate: any;
  startTime: any;
  endDate: any;
  endTime: any;
  descriptionFile?: string;
  ehsFile?: string;
}

interface PtwCreateComponentProps {
  i18n: typeof i18n;
  t: typeof i18n.t;
  ptw?: IPtw;
  project: IProject;
  buildings: IBuilding[];
  workstations: IWorkstation[];
  resetServerRequestAction: typeof resetServerRequestAction;
  buildingsAction: typeof buildingsAction;
  workstationsAction: typeof workstationsAction;
  ptwCreateAction: typeof ptwCreateAction;
  ptwEditAction: typeof ptwEditAction;
  push: Push;
  serverRequest?: ServerRequestStatus;
  onComplete?: () => void;
  user: IUser;
}

class PPtwCreateComponent extends Component<PtwCreateComponentProps> {
  formikApi: any = null;

  componentDidMount() {
    this.props.buildingsAction();
    this.props.workstationsAction();
  }

  componentDidUpdate(prevProps: Readonly<PtwCreateComponentProps>): void {
    if (
      this.props.serverRequest &&
      prevProps.serverRequest &&
      !this.props.serverRequest.pending &&
      this.props.serverRequest.pending !== prevProps.serverRequest.pending
    ) {
      this.formikApi.setSubmitting(false);

      if (this.props.onComplete) {
        this.props.onComplete();
      }

      this.props.resetServerRequestAction(ActionType.PTW_CREATE);
      this.props.resetServerRequestAction(ActionType.PTW_EDIT);
      this.props.push('./');
    }
  }

  initialValues = (): PtwCreateValues => {
    if (this.props.ptw) {
      const {
        _id,
        name,
        train,
        vehicles,
        workstation,
        building,
        company,
        employees,
        descriptionOfOperations,
        risk,
        startTimestamp,
        endTimestamp,
        files,
        ehs,
        pc9,
      } = this.props.ptw;

      const descriptionFile = files ? (files as unknown as Record<string, IPtwFile>).descriptionFile : null;
      const ehsFile = files ? (files as unknown as Record<string, IPtwFile>).ehsFile : null;

      return {
        _id,
        name,
        train,
        vehicles,
        workstation,
        building,
        company,
        employees,
        descriptionOfOperations,
        risk,
        ehs,
        pc9,

        startDate: startTimestamp,
        startTime: moment(startTimestamp),
        endDate: endTimestamp,
        endTime: moment(endTimestamp),
        descriptionFile: descriptionFile ? descriptionFile.originalName : null,
        ehsFile: ehsFile ? ehsFile.originalName : null,
      };
    } else {
      return {
        name: '',
        train: '',
        vehicles: [],
        workstation: '',
        building: '',
        company: '',
        descriptionOfOperations: '',
        risk: false,
        startDate: null,
        startTime: null,
        endDate: null,
        endTime: null,
        descriptionFile: '',
        ehsFile: '',
        ehs: '',
        pc9: 'Pre PC9',
      };
    }
  };

  mapTrainOptions = (trains: ITrain[]) => {
    return trains.map((train) => ({
      key: train._id,
      text: train.name,
      value: train._id,
    }));
  };

  vehiclesForSelectedTrain = (trainId: string = null) => {
    const train = this.props.project.trains.find((train) => train._id === trainId);
    return train && trainId ? train.vehicles : [];
  };

  mapVehicleOptions = (vehicles: IVehicle[]) => {
    return vehicles.map((vehicle) => ({
      key: vehicle._id,
      text: vehicle.name,
      value: vehicle._id,
    }));
  };

  mapBuildingOptions = (buildings: IBuilding[]) => {
    return buildings.map((building) => ({
      key: building._id,
      text: building.name,
      value: building._id,
    }));
  };

  mapWorkstationOptions = (workstations: IWorkstation[]) => {
    return workstations.map((workstation) => ({
      key: workstation._id,
      text: workstation.name,
      value: workstation._id,
    }));
  };

  handleSubmit = (values: PtwCreateValues, formikApi: any) => {
    const { project } = this.props;

    this.formikApi = formikApi;

    const {
      _id,
      status,
      building,
      company,
      descriptionOfOperations,
      employees,
      endDate,
      endTime,
      descriptionFile,
      ehsFile,
      name,
      risk,
      startDate,
      startTime,
      train,
      vehicles,
      workstation,
      ehs,
      pc9,
    } = values;

    const momentStartDate = moment(startDate);
    const startTimestamp = moment({
      year: momentStartDate.year(),
      month: momentStartDate.month(),
      day: momentStartDate.date(),
      hour: startTime.hour(),
      minute: startTime.minute(),
      second: 0,
      millisecond: 0,
    });

    const momentEndDate = moment(endDate);
    const endTimestamp = moment({
      year: momentEndDate.year(),
      month: momentEndDate.month(),
      day: momentEndDate.date(),
      hour: endTime.hour(),
      minute: endTime.minute(),
      second: 0,
      millisecond: 0,
    });

    const multiDay = moment.duration(endTimestamp.diff(startTimestamp)).days() >= 1;

    if (!!_id) {
      this.props.ptwEditAction({
        _id,
        building,
        company,
        descriptionOfOperations,
        employees,
        endTimestamp,
        multiDay,
        name,
        projectId: project._id,
        risk,
        startTimestamp,
        train,
        vehicles,
        workstation,
        pc9,
        ehs: ehs === '' ? undefined : ehs,
      });
    } else {
      this.props.ptwCreateAction(
        {
          status,
          building,
          company,
          descriptionOfOperations,
          employees,
          endTimestamp,
          multiDay,
          name,
          projectId: project._id,
          risk,
          startTimestamp,
          train,
          vehicles,
          workstation,
          pc9,
          ehs: ehs === '' ? undefined : ehs,
        },
        { descriptionFile, ehsFile }
      );
    }
  };

  renderSendButton = (setFieldValue: any, submitForm: () => void) => {
    const { ptw } = this.props;
    if (typeof ptw === 'undefined') {
      return (
        <Button
          primary
          onClick={() => {
            setFieldValue('status', EPtwStatus.ACCETTAZIONE_R);
            submitForm();
          }}
        >
          <TranslationComponent stringKey={'Send'} />
        </Button>
      );
    } else {
      return null;
    }
  };

  endTimeValidator = {
    disabledHours: (startDate: any, endDate: any, startTime: any) => {
      if (moment(startDate).isSame(moment(endDate), 'day')) {
        return [...new Array(moment(startTime).get('hour'))].map((value, index) => index);
      } else {
        return [];
      }
    },
    disabledMinutes: (startDate: any, endDate: any, startTime: any, hour: any) => {
      if (moment(startDate).isSame(moment(endDate), 'day') && moment(startTime).get('hour') === hour) {
        return [...new Array(moment(startTime).get('minutes') + 1)].map((value, index) => index);
      } else {
        return [];
      }
    },
  };

  render() {
    const { project, buildings = [], workstations = [], serverRequest, onComplete, t, user } = this.props;

    return (
      <Container>
        <Form
          initialValues={this.initialValues()}
          validationSchema={PtwCreateSchema(t)}
          onSubmit={this.handleSubmit}
          render={({ handleReset, setFieldValue, values, touched, errors, submitForm }) => (
            <Form.Children>
              <Input label={t('Activity Name')} name="name" />

              <Form.Group>
                <DatePicker label={t('Start date')} name="startDate" inputProps={{ placeholder: '' }} />
                <TimePickerComponent
                  label={t('Start time')}
                  name="startTime"
                  inputProps={{ showSecond: false, defaultValue: values.startTime }}
                />
              </Form.Group>

              <Form.Group>
                <DatePicker
                  label={t('End')}
                  name="endDate"
                  inputProps={{ placeholder: '', isOutsideRange: (day) => day.isBefore(moment(values.startDate)) }}
                />
                <TimePickerComponent
                  label={t('End time')}
                  name="endTime"
                  inputProps={{
                    showSecond: false,
                    defaultValue: values.endTime,
                    disabledHours: () =>
                      this.endTimeValidator.disabledHours(values.startDate, values.endDate, values.startTime),
                    disabledMinutes: (hour) =>
                      this.endTimeValidator.disabledMinutes(values.startDate, values.endDate, values.startTime, hour),
                  }}
                />
              </Form.Group>

              <Dropdown
                label={t('Train')}
                name="train"
                options={this.mapTrainOptions(project.trains)}
                inputProps={{
                  error: touched.train && !!errors.train,
                  onChange: () => {
                    setFieldValue('vehicles', []);
                  },
                }}
              />

              <Dropdown
                label={t('Vehicle')}
                name="vehicles"
                options={this.mapVehicleOptions(this.vehiclesForSelectedTrain(values.train))}
                inputProps={{ error: touched.vehicles && !!errors.vehicles, disabled: !values.train, multiple: true }}
              />

              <Dropdown
                label={t('Building')}
                name="building"
                options={this.mapBuildingOptions(buildings)}
                inputProps={{ error: touched.building && !!errors.building }}
              />
              <Dropdown
                label={t('Workstations')}
                name="workstation"
                options={this.mapWorkstationOptions(workstations)}
                inputProps={{ error: touched.workstation && !!errors.workstation }}
              />
              <Input label={t('Company')} name="company" />
              <TextArea label={t('People (name, surname, alps id)')} name="employees" />
              <TextArea label={t('Description')} name="descriptionOfOperations" />
              <Dropdown
                inputProps={{
                  placeholder: 'PC9',
                  fluid: true,
                }}
                name={'pc9'}
                options={[
                  { value: 'Pre PC9', text: 'Pre PC9' },
                  { value: 'Post PC9', text: 'Post PC9' },
                ]}
              />

              <label htmlFor="description_file">
                <TranslationComponent stringKey={'Attachment'} />
              </label>
              <input
                id="description_file"
                type="file"
                name="descriptionFile"
                onChange={(e) => {
                  setFieldValue('descriptionFile', e.target.files[0]);
                }}
              />
              {touched.descriptionFile && !!errors.descriptionFile && (
                <Message content={errors.descriptionFile} negative />
              )}

              <Checkbox label={t('High risk work')} name="risk" />

              <EhsCreateModal buildings={buildings} ehs={values.ehs} onEhs={({ _id }) => setFieldValue('ehs', _id)} />

              <br />
              <br />

              {serverRequest && serverRequest.success && <Message positive content="PTW created" />}
              {serverRequest && serverRequest.error && (
                <Message negative header="Error" content={serverRequest.error} />
              )}
              {this.renderSendButton(setFieldValue, submitForm)}
              <Button primary onClick={submitForm}>
                <TranslationComponent stringKey={'Save'} />
              </Button>
              <Button
                basic
                onClick={() => {
                  handleReset();
                  if (onComplete) {
                    onComplete();
                  }
                }}
              >
                <TranslationComponent stringKey={'Cancel'} />
              </Button>
            </Form.Children>
          )}
        />
      </Container>
    );
  }
}

const mapStateToProps = (state: RootState, ownProps: any) => ({
  project: appState(state).project,
  buildings: appState(state).buildings,
  workstations: appState(state).workstations,
  user: appState(state).user,
  serverRequest: appStateServerRequests(
    state,
    ownProps.ptw && ownProps.ptw._id ? ActionType.PTW_EDIT : ActionType.PTW_CREATE
  ),
});

export const PtwCreateComponent = connect(mapStateToProps, {
  resetServerRequestAction,
  buildingsAction,
  workstationsAction,
  ptwCreateAction,
  ptwEditAction,
  push,
})(withTranslation()(PPtwCreateComponent));
